'use client';

import { useEffect, useState } from 'react';

interface DeadlineInfo {
  deadline: Date;
  deadlineDisplay: string;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
}

/**
 * 첫 렌더 값. 서버 렌더와 브라우저의 첫 렌더가 같아야 한다 — 남은 시간을 그 자리에서 계산하면
 * 서버 시각과 브라우저 시각의 초가 달라 매번 React 418(hydration 불일치)이 났다.
 * 화면에 붙은 직후 useEffect 가 실제 남은 시간으로 바꾼다.
 */
const PENDING_DEADLINE: DeadlineInfo = {
  deadline: new Date(0),
  deadlineDisplay: '--/--',
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
  isExpired: false,
};

const calculateDeadline = (): DeadlineInfo => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-indexed
  const date = now.getDate();

  // 이번달 말일 계산
  const lastDay = new Date(year, month + 1, 0).getDate();

  // 이번달 15일 23:59:59
  const thisMonth15th = new Date(year, month, 15, 23, 59, 59, 999);
  // 이번달 말일 23:59:59
  const thisMonthLastDay = new Date(year, month, lastDay, 23, 59, 59, 999);

  // 오늘이 15일 이하인 경우 → 이번달 15일이 마감
  // 오늘이 16일 이후인 경우 → 이번달 말일이 마감
  let candidateDeadline: Date;
  let candidateDisplay: string;

  if (date <= 15) {
    candidateDeadline = thisMonth15th;
    candidateDisplay = `${String(month + 1).padStart(2, '0')}/15`;
  } else {
    candidateDeadline = thisMonthLastDay;
    candidateDisplay = `${String(month + 1).padStart(2, '0')}/${String(lastDay).padStart(2, '0')}`;
  }

  // 선택한 마감일이 이미 지났다면, 다음 기간으로 넘어감
  let deadline: Date;
  let deadlineDisplay: string;

  if (now.getTime() > candidateDeadline.getTime()) {
    // 마감일이 지났으므로 다음 기간으로 설정
    if (date <= 15) {
      // 15일이 지났으므로 말일로
      deadline = thisMonthLastDay;
      deadlineDisplay = `${String(month + 1).padStart(2, '0')}/${String(lastDay).padStart(2, '0')}`;
    } else {
      // 말일이 지났으므로 다음달 15일로
      const nextMonth = month === 11 ? 0 : month + 1;
      const nextMonthYear = month === 11 ? year + 1 : year;
      deadline = new Date(nextMonthYear, nextMonth, 15, 23, 59, 59, 999);
      deadlineDisplay = `${String(nextMonth + 1).padStart(2, '0')}/15`;
    }
  } else {
    // 아직 마감일 전이므로 선택한 마감일 사용
    deadline = candidateDeadline;
    deadlineDisplay = candidateDisplay;
  }

  // 남은 시간 계산
  const diff = deadline.getTime() - now.getTime();
  const isExpired = diff < 0;

  let days = 0,
    hoursRemaining = 0,
    minutesRemaining = 0,
    secondsRemaining = 0;

  if (!isExpired) {
    days = Math.floor(diff / (1000 * 60 * 60 * 24));
    hoursRemaining = Math.floor(
      (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
    );
    minutesRemaining = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    secondsRemaining = Math.floor((diff % (1000 * 60)) / 1000);
  }

  return {
    deadline,
    deadlineDisplay,
    days,
    hours: hoursRemaining,
    minutes: minutesRemaining,
    seconds: secondsRemaining,
    isExpired,
  };
};

interface DeadlineCountdownProps {
  title: string;
  titleColor?: string;
  deadlineDisplay?: string;
  countdownBgColor?: string;
  countdownTextColor?: string;
  boxShadowColor?: string;
}

export default function DeadlineCountdown({
  title,
  titleColor = '#111111',
  deadlineDisplay,
  countdownBgColor = '#2E2D2D',
  countdownTextColor = '#FFFFFF',
  boxShadowColor = '#000',
}: DeadlineCountdownProps) {
  const [deadlineInfo, setDeadlineInfo] =
    useState<DeadlineInfo>(PENDING_DEADLINE);

  useEffect(() => {
    setDeadlineInfo(calculateDeadline());

    // 1초마다 업데이트
    const interval = setInterval(() => {
      setDeadlineInfo(calculateDeadline());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <div
        className="typo-16 desktop:typo-24 font-bold"
        style={{ color: titleColor }}
      >
        {title} (~{deadlineDisplay || deadlineInfo.deadlineDisplay})
      </div>
      <div className="flex items-center justify-center gap-2">
        <span
          className="typo-20 desktop:typo-32 desktop:w-[140px] desktop:h-[80px] desktop:rounded-lg flex h-[48px] w-[72px] items-center justify-center rounded-[4px] font-bold tabular-nums"
          style={{
            backgroundColor: countdownBgColor,
            color: countdownTextColor,
            boxShadow: `0 2px 8px 0 ${boxShadowColor} inset`,
          }}
        >
          {deadlineInfo.isExpired ? '0' : deadlineInfo.days}일
        </span>
        <span
          className="typo-20 desktop:typo-32 desktop:w-[140px] desktop:h-[80px] desktop:rounded-lg flex h-[48px] w-[72px] items-center justify-center rounded-[4px] font-bold tabular-nums"
          style={{
            backgroundColor: countdownBgColor,
            color: countdownTextColor,
            boxShadow: `0 2px 8px 0 ${boxShadowColor} inset`,
          }}
        >
          {deadlineInfo.isExpired
            ? '00'
            : String(deadlineInfo.hours).padStart(2, '0')}
          시간
        </span>
        <span
          className="typo-20 desktop:typo-32 desktop:w-[140px] desktop:h-[80px] desktop:rounded-lg flex h-[48px] w-[72px] items-center justify-center rounded-[4px] font-bold tabular-nums"
          style={{
            backgroundColor: countdownBgColor,
            color: countdownTextColor,
            boxShadow: `0 2px 8px 0 ${boxShadowColor} inset`,
          }}
        >
          {deadlineInfo.isExpired
            ? '00'
            : String(deadlineInfo.minutes).padStart(2, '0')}
          분
        </span>
        <span
          className="typo-20 desktop:typo-32 desktop:w-[140px] desktop:h-[80px] desktop:rounded-lg flex h-[48px] w-[72px] items-center justify-center rounded-[4px] font-bold tabular-nums"
          style={{
            backgroundColor: countdownBgColor,
            color: countdownTextColor,
            boxShadow: `0 2px 8px 0 ${boxShadowColor} inset`,
          }}
        >
          {deadlineInfo.isExpired
            ? '00'
            : String(deadlineInfo.seconds).padStart(2, '0')}
          초
        </span>
      </div>
    </>
  );
}
